import { useParams } from 'react-router-dom';
import { Tile } from 'components/Layout/PageContainer';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { useEffect } from 'react';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { BreadcrumbItem } from 'components/breadcrumb/breadcrumbModel';
import { useCategoryProductCount } from 'pages/entities/queryHooks';
import { EntityPage } from 'pages/entities/components/EntityPage';
import { Filters } from 'services/data.service';
import { EntityHeader } from 'pages/entities/components/EntityHeader';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';
import { useTranslatedAttribute } from 'pages/trading/queryHooks';
import { FormattedMessage } from 'react-intl';
import { useModelService } from 'services/model/model.service';

export function CategoryEntityPage() {
  const { category } = useParams();
  const { getAttributes } = useModelService();

  const { data: translatedCategory } = useTranslatedAttribute('prdCategory', category ?? '');

  // Fetch product attributes without sales-dimension filters so the summary
  // tile always shows product info (name, colors, sizes, etc.) even when the
  // filtered sales data returns no results
  const { data, isError } = useCategoryProductCount(category ?? '');

  // replace the product number in breadcrumb by the product name when data is loaded
  useEffect(() => {
    if (category) {
      const { breadcrumbs, setBreadcrumbs } = useBreadcrumbStore.getState();

      const updateProductBreadcrumb = (items: BreadcrumbItem[]): BreadcrumbItem[] =>
        items.map(item => {
          if (item.path?.includes(category)) {
            return {
              ...item,
              label: translatedCategory,
            };
          }

          return item;
        });

      const updatedBreadcrumbs = updateProductBreadcrumb(breadcrumbs);
      setBreadcrumbs(updatedBreadcrumbs);
    }
  }, [data, category]);

  if (!(data && category)) {
    return <MetricsLoadingSpinner />;
  }

  const filterDecorator = (filters: Filters) => {
    return { ...filters, categories: [category] };
  };

  const availableTables = getAttributes(['chnMarketplace', 'chnCountry', 'prdBrand', 'prdNumber']);

  return (
    <EntityPage
      filterDecorator={filterDecorator}
      availableTables={availableTables}
      disabledFilters={['prdCategory']}
      showCostOfGoods={false}
    >
      {isError ? (
        <Tile title={category} isError={isError} />
      ) : (
        <EntityHeader
          entity={'category'}
          entityDisplayName={
            <div className={`flex gap-1 items-center`}>
              {findIconForCategory(category, 20)}
              {translatedCategory}
            </div>
          }
          custom={
            <div className="text-[14px]">
              <span className="font-semibold">
                <FormattedMessage id="entities.categories.product.count" />
              </span>
              <span className="pl-2">{`(${data ?? ''})`}</span>
            </div>
          }
        />
      )}
    </EntityPage>
  );
}
